import { Grid } from '@material-ui/core';
import clsx from 'clsx';
import { default as React, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch } from 'react-redux';
import { UnitOfMeasureTypes } from 'src/common/enumerations/unit-of-measure-type';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { getValueAsString } from 'src/common/helpers/string-helper';
import { ILineItem } from 'src/common/interfaces/dtos/ILineItem';
import { ItemListActions } from 'src/common/redux/reducers/item-list-reducer';
import { MiddleAreaActions } from 'src/common/redux/reducers/middle-area-reducer';
import { TenderIcon } from 'src/customer-display/components/tender-icon';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { EJRecordTypes } from '../../common/enumerations/ej-record-types';
import { IElectronicJournalRecord } from '../../common/interfaces/states/IElectronicJournalRecord';
import { ReactComponent as ItemIndicator } from '../../common/media/EBT.svg';
import { ReactComponent as TareLink } from '../../common/media/link.svg';
import { ReactComponent as ReturnItemIcon } from '../../common/media/return-item-icon.svg';
import { saleQuantityCount } from '../../common/helpers/item-quantity-measure';
import { tbrItemClick } from '../helpers/tbr-item-click';
import {
  SvgCoupon,
  SvgCouponActive,
  SvgPromotionsStar,
  SvgRewardAsTender,
} from './item-list-icons';
import { styles } from './item-list-styles';
import TBRCheckbox from './tbr-checkbox';
import { getCheckboxStatus } from './tbr-list-helper';
import { themeColor } from 'src/mui-theme';
import { store } from '../../common/redux/configure-store';
import { ItemCategories } from '../../common/enumerations/rti-enums/item-categories';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';

const LineIcon: React.FC<LineIconProps> = ({
  type,
  selected,
  isTBRActive,
  pageSize,
  disabled,
}: LineIconProps): JSX.Element | null => {
  const classes = styles({ pageSize, disabled });

  switch (type) {
    case EJRecordTypes.Promotion: {
      return isTBRActive ? (
        <SvgPromotionsStar
          fill='#B3B8BC'
          className={classes.inlineIcon}
          data-testid='disabled-promotion-icon'
        />
      ) : selected ? (
        <SvgPromotionsStar
          className={classes.inlineIconSelected}
          data-testid='selected-promotion-icon'
        />
      ) : (
        <SvgPromotionsStar className={classes.inlineIcon} data-testid='unselected-promotion-icon' />
      );
    }
    case EJRecordTypes.Coupon: {
      return selected ? (
        <SvgCoupon className={classes.inlineIcon} data-testid='selected-coupon-icon' />
      ) : (
        <SvgCouponActive
          className={classes.inlineIconSelected}
          data-testid='unselected-coupon-icon'
        />
      );
    }
    case EJRecordTypes.Tender:
    case EJRecordTypes.RewardAsTender: {
      return (
        <SvgRewardAsTender
          className={selected ? classes.inlineIconSelected : classes.inlineIcon}
          data-testid='rewardAsTender-icon'
          fill={themeColor.secondaryMain}
        />
      );
    }
    case EJRecordTypes.ReturnItem: {
      return <ReturnItemIcon className={classes.returnItem} />;
    }
    case EJRecordTypes.Indicator: {
      return (
        <ItemIndicator className={selected ? classes.inlineIconSelected : classes.inlineIcon} />
      );
    }
    default:
      return null;
  }
};

const ItemListLine: React.FC<ItemListLineProps> = (props: ItemListLineProps): JSX.Element => {
  const {
    item,
    showSelectedItem,
    isCustomerDisplay,
    linkedItem,
    trainingModeActive,
    isTBRActive,
    tbrItemsList,
    pageSize,
    disabled,
  } = props;

  const classes = styles({ pageSize, disabled });
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const clickItem = useCallback((): void => {
    batch((): void => {
      if (!item.Selected) {
        dispatch(ItemListActions.selectItem(item.SequenceNumber, item.Type, item.PromotionId));
      }
      dispatch(MiddleAreaActions.setMiddleAreaState({ tabValue: 0 }));
    });
  }, [item, dispatch]);
  const clickTbrItem = useCallback((): void => {
    tbrItemClick(item, tbrItemsList).catch(popupMessage);
  }, [item, tbrItemsList]);

  const { showTareLinkedItemOnEJ } = getCurrentXLRConfig();
  const allowToShowLinkedItems = !!store.getState().touchPoint.touchPointConfiguration
    .depositConfigurations?.displayDepositItemOnCashierDisplayAndCustomerDisplay;
  const isDepositLinkedItem = item.LinkedLines?.some(
    item => item.ItemType === ItemCategories.Deposit,
  );
  const isTareLinkedItem = item.LinkedLines?.some(item => item.ItemType === ItemCategories.Tare);
  const price =
    (!allowToShowLinkedItems && isDepositLinkedItem) ||
    (!showTareLinkedItemOnEJ && isTareLinkedItem)
      ? item.Price
      : item.ExtendedAmount && !item.IsMsu
      ? item.ExtendedAmount
      : item.Price;

  const LineContent: React.FC<LineContentProps> = (props: LineContentProps): JSX.Element => {
    const { item, isTBRActive, isCustomerDisplay } = props;
    const nameClass: string = '';

    if (linkedItem) {
      const isTareOverride = item?.TareWeight?.IsTareOverride;
      const linkedLineWeightText = isTareOverride
        ? `1`
        : `${item?.Quantity} ${
            item.UnitOfMeasureCode === UnitOfMeasureTypes.Each ? '' : item.UnitOfMeasureCode
          }`;
      const linkedLineText = `${linkedItem.Description}`;
      const linkedLinePrice =
        linkedItem.Amount !== undefined ? convertCurrency(linkedItem.Amount, undefined, false) : '';

      return (
        <>
          <Grid container item xs={7} className={classes.lineItemTitleContainer}>
            <Grid
              item
              className={clsx(classes.iconContainer, 'fullHeight')}
              data-testid={'link-item-indicator'}
            >
              <TareLink
                className={
                  showSelectedItem && item.Selected
                    ? classes.inlineIconSelected
                    : classes.inlineIcon
                }
              />
            </Grid>
            <Grid item className={classes.lineItemTitle} data-testid={`link-item-name`}>
              {`${linkedLineText}`}
            </Grid>
          </Grid>
          <Grid
            item
            xs={2}
            className={`${classes.textCenter} ${classes.lineItemFont} `}
            data-testid={`link-item-qty`}
          >
            {isTBRActive && !isCustomerDisplay ? item.tbrAvailableQty : linkedLineWeightText}
          </Grid>
          <Grid
            item
            xs={3}
            className={`${classes.textRight} ${classes.lineItemFont}`}
            data-testid={`link-item-price`}
          >
            {item.Type === EJRecordTypes.ReturnItem && '-'}
            {linkedLinePrice}
          </Grid>
        </>
      );
    }
    switch (item.Type) {
      case EJRecordTypes.Product: {
        return (
          <>
            <Grid container className='fullHeight'>
              <Grid item container className='fullHeight'>
                <Grid container item xs={6} className={classes.lineItemTitleContainer}>
                  <Grid
                    item
                    className={clsx(classes.productIconContainer, classes.iconContainer)}
                    data-testid={'cart-item-indicator'}
                  >
                    {item.isSnapItem && (
                      <LineIcon
                        type={EJRecordTypes.Indicator}
                        selected={showSelectedItem && item.Selected}
                        pageSize={pageSize}
                        disabled={disabled}
                        isTBRActive={isTBRActive}
                      />
                    )}
                    {item.tbrReturnItem && isTBRActive && (
                      <TBRCheckbox checked={getCheckboxStatus(item)} />
                    )}
                  </Grid>
                  <Grid item className={classes.lineItemTitle} data-testid={`cart-item-name`}>
                    {item.Name}
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={3}
                  className={`${classes.textCenter} ${classes.lineItemFont}`}
                  data-testid={`cart-item-quantity`}
                >
                  {saleQuantityCount(item)}
                </Grid>
                <Grid
                  item
                  xs={3}
                  className={`${classes.textRight} ${classes.lineItemFont}`}
                  data-testid={`cart-item-price`}
                >
                  {price}
                </Grid>
              </Grid>
            </Grid>
          </>
        );
      }
      case EJRecordTypes.ReturnItem: {
        return (
          <>
            <Grid container item xs={7} className={classes.lineItemTitleContainer}>
              <Grid item className={clsx(classes.iconContainer, 'fullHeight')}>
                <LineIcon
                  type={EJRecordTypes.ReturnItem}
                  selected={showSelectedItem && item.Selected}
                  pageSize={pageSize}
                  disabled={disabled}
                  isTBRActive={isTBRActive}
                />
              </Grid>
              <Grid item className={classes.lineItemTitle} data-testid={`cart-item-name`}>
                {item.Name}
              </Grid>
            </Grid>
            <Grid
              item
              xs={2}
              className={`${classes.textCenter} ${classes.lineItemFont}`}
              data-testid={`cart-item-quantity`}
            >
              {saleQuantityCount(item)}
            </Grid>
            <Grid
              item
              xs={3}
              className={`${classes.textRight} ${classes.lineItemFont}`}
              data-testid={`cart-item-price`}
            >
              -{price}
            </Grid>
          </>
        );
      }
      case EJRecordTypes.Coupon: {
        return (
          <>
            <Grid container item xs={7} className={classes.lineItemTitleContainer}>
              <Grid
                item
                className={clsx(classes.iconContainer, 'fullHeight')}
                data-testid={'cart-item-indicator'}
              >
                <LineIcon
                  type={EJRecordTypes.Coupon}
                  selected={showSelectedItem && item.Selected}
                  pageSize={pageSize}
                  disabled={disabled}
                  isTBRActive={isTBRActive}
                />
              </Grid>
              <Grid item className={classes.lineItemTitle} data-testid={`cart-item-name`}>
                {`${t('Coupon_LineDisplay')} ${item.Name}`}
              </Grid>
            </Grid>
            <Grid
              item
              xs={2}
              className={`${classes.textCenter} ${classes.lineItemFont}`}
              data-testid={`cart-item-quantity`}
            >
              {item.Quantity && item.Quantity > 1 ? item.Quantity : ''}
            </Grid>
          </>
        );
      }
      case EJRecordTypes.Tender: {
        const ejItem: IElectronicJournalRecord = props.item;
        const icon = getValueAsString(
          ejItem.Type === EJRecordTypes.RewardAsTender ? ejItem.Type : ejItem.TenderType,
        );
        return (
          <>
            <Grid container item xs={9} className={classes.lineItemTitleContainer}>
              <Grid
                item
                className={clsx(classes.iconContainer, 'fullHeight')}
                data-testid={'cart-item-indicator'}
              >
                <TenderIcon
                  tenderType={icon}
                  disabled={disabled}
                  isCustomerDisplay={isCustomerDisplay}
                />
              </Grid>
              <Grid item className={classes.lineItemTitle} data-testid={`cart-item-name`}>
                {item.Name}
              </Grid>
            </Grid>
            <Grid
              item
              xs={3}
              className={`${classes.textRight} ${classes.lineItemFont}`}
              data-testid={`cart-item-price`}
            >
              {price}
            </Grid>
          </>
        );
      }
      case EJRecordTypes.TaxExemption: {
        return (
          <>
            <Grid container item className={classes.lineItemTitleContainer}>
              <Grid
                item
                className={clsx(classes.iconContainer, 'fullHeight')}
                data-testid='cart-item-indicator'
              >
                <LineIcon
                  type={EJRecordTypes.TaxExemption}
                  selected={showSelectedItem && item.Selected}
                  pageSize={pageSize}
                  disabled={disabled}
                  isTBRActive={isTBRActive}
                />
              </Grid>
              <Grid item className={classes.lineItemTitle} data-testid='cart-item-name'>
                {item.Savings && t('LineDisplay_TaxExempt', { arg: item.Savings })}
              </Grid>
            </Grid>
          </>
        );
      }
      case EJRecordTypes.ItemFee:
        return (
          <DefaultItemLine
            item={item}
            showSelectedItem
            pageSize={pageSize}
            disabled={disabled}
            isTBRActive={isTBRActive}
            nameClass={classes.upperCase}
          />
        );
      case EJRecordTypes.TransactionFee:
        return (
          <DefaultItemLine
            item={item}
            showSelectedItem
            pageSize={pageSize}
            disabled={disabled}
            isTBRActive={isTBRActive}
            nameClass={classes.upperCase}
          />
        );
      case EJRecordTypes.TenderEligibility:
        return (
          <TenderEligibilityLine
            item={item}
            showSelectedItem
            pageSize={pageSize}
            disabled={disabled}
            isTBRActive={isTBRActive}
          />
        );
      default:
        return (
          <DefaultItemLine
            item={item}
            showSelectedItem
            pageSize={pageSize}
            disabled={disabled}
            isTBRActive={isTBRActive}
            nameClass={nameClass}
          />
        );
    }
  };

  return (
    <Grid
      item
      className={`${item.Type === EJRecordTypes.Tender ? classes.line : classes.lineItem}
        ${
          item.LinkedLines && item.LinkedLines.length > 0 && !linkedItem
            ? classes.noBottomBorder
            : ''
        } ${!disabled ? classes.clickable : ''}`}
      data-testid={`cart-${linkedItem ? 'linked-' : ''}item-${item.Type}`}
      onClick={!disabled ? (isTBRActive ? clickTbrItem : clickItem) : undefined}
    >
      <Grid
        container
        justifyContent='space-between'
        alignItems='center'
        className={`fullHeight 
          ${classes.lineItemContainer} 
          ${
            showSelectedItem && item.Selected
              ? trainingModeActive
                ? `${classes.customerDisplaySelected} selected-item`
                : `${classes.selected} selected-item`
              : ''
          }
          ${isCustomerDisplay ? `${classes.lineItemContainerCustomer}` : ''}
        `}
      >
        {item.Voided ? <div className={`${classes.voided} voided-line`}></div> : null}
        <LineContent item={item} isTBRActive={isTBRActive} isCustomerDisplay={isCustomerDisplay} />
      </Grid>
    </Grid>
  );
};

const TenderEligibilityLine: React.FC<TenderEligibilityLineProps> = ({
  item,
  showSelectedItem,
  pageSize,
  disabled,
  isTBRActive,
}: TenderEligibilityLineProps): JSX.Element => {
  const classes = styles({});
  const { t } = useTranslation();

  return (
    <>
      <Grid container item xs={9} className={classes.lineItemTitleContainer}>
        <Grid
          item
          className={clsx(classes.iconContainer, 'fullHeight')}
          data-testid={'cart-item-indicator'}
        >
          <LineIcon
            type={item.Type}
            selected={showSelectedItem && item.Selected}
            pageSize={pageSize}
            disabled={disabled}
            isTBRActive={isTBRActive}
          />
        </Grid>
        <Grid item className={classes.lineItemTitle} data-testid={`cart-item-tender-eligibility`}>
          {`${t(item.Name)} ${t('TenderEligibilityLimit')} (${item.Price})`}
        </Grid>
      </Grid>
    </>
  );
};

const DefaultItemLine: React.FC<DefaultItemLineProps> = ({
  item,
  showSelectedItem,
  nameClass,
  pageSize,
  disabled,
  isTBRActive,
}: DefaultItemLineProps): JSX.Element => {
  const classes = styles({});
  const { t } = useTranslation();

  return (
    <>
      <Grid container item xs={9} className={classes.lineItemTitleContainer}>
        <Grid
          item
          className={clsx(classes.iconContainer, 'fullHeight')}
          data-testid={'cart-item-indicator'}
        >
          <LineIcon
            type={item.Type}
            selected={showSelectedItem && item.Selected}
            pageSize={pageSize}
            disabled={disabled}
            isTBRActive={isTBRActive}
          />
        </Grid>
        <Grid
          item
          className={clsx(classes.lineItemTitle, nameClass)}
          data-testid={`cart-item-name`}
        >
          {t(item.Name)}
        </Grid>
      </Grid>
      <Grid
        item
        xs={3}
        className={`${classes.textRight} ${classes.lineItemFont}`}
        data-testid={`cart-item-price`}
      >
        {item.Price}
      </Grid>
    </>
  );
};

interface DefaultItemLineProps {
  item: IElectronicJournalRecord;
  showSelectedItem: boolean;
  pageSize: number;
  disabled?: boolean;
  isTBRActive?: boolean;
  nameClass: string;
}

interface TenderEligibilityLineProps {
  item: IElectronicJournalRecord;
  showSelectedItem: boolean;
  pageSize: number;
  disabled?: boolean;
  isTBRActive?: boolean;
}

interface LineIconProps {
  type: EJRecordTypes;
  selected: boolean;
  isTBRActive?: boolean;
  pageSize: number;
  disabled?: boolean;
}

interface LineContentProps {
  item: IElectronicJournalRecord;
  isTBRActive?: boolean;
  isCustomerDisplay?: boolean;
}

interface ItemListLineProps {
  item: IElectronicJournalRecord;
  showSelectedItem: boolean;
  isCustomerDisplay?: boolean;
  linkedItem?: ILineItem;
  trainingModeActive?: boolean;
  isTBRActive?: boolean;
  tbrItemsList: IElectronicJournalRecord[];
  pageSize: number;
  disabled?: boolean;
}

export default ItemListLine;
